import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { RequestSummaryCustomType } from '@coin/shared/core/util-enums';
import { MassReqResponse, TransactionStatusMetadata } from '@coin/shared/core/util-models';
import { DownloadFileHelper, TinyHelpers } from '@coin/shared/core/util-helpers';
import { Observable } from 'rxjs';

interface Data {
  result: MassReqResponse | TransactionStatusMetadata;
  customType?: RequestSummaryCustomType;
  customHeaderTitle?: string;
  customFailureCountTitle?: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-request-summary',
  templateUrl: './request-summary.component.html',
  styleUrls: ['./request-summary.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestSummaryComponent {
  private dialogRef = inject<MatDialogRef<RequestSummaryComponent>>(MatDialogRef);
  data = inject<Data>(MAT_DIALOG_DATA);

  public customType = RequestSummaryCustomType;

  public areWarningsVisible = false;
  public areFailuresVisible = true;

  getAccumulatedSuccessCount(response: MassReqResponse): number {
    return response?.updatedCount + response?.skippedCount + response?.skipCount + response?.createdCount || 0;
  }

  public static openDialog(dialog: MatDialog, data: Data): Observable<undefined> {
    return dialog
      .open<RequestSummaryComponent, Data, undefined>(RequestSummaryComponent, {
        disableClose: true,
        data
      })
      .beforeClosed();
  }

  public getItemOverviewText(item: unknown): string {
    return Object.keys(item)
      .map(key => `${TinyHelpers.camelCaseToText(key)}: ${Array.isArray(item[key]) ? item[key].join(', ') : item[key]}`)
      .join(', ');
  }

  close(): void {
    this.dialogRef.close();
  }

  public toggleWarningsView(): void {
    this.areFailuresVisible = this.areWarningsVisible;
    this.areWarningsVisible = !this.areWarningsVisible;
  }

  public toggleFailuresView(): void {
    this.areWarningsVisible = this.areFailuresVisible;
    this.areFailuresVisible = !this.areFailuresVisible;
  }

  private isTransactionStatusMetadata(
    result: MassReqResponse | TransactionStatusMetadata
  ): result is TransactionStatusMetadata {
    return 'warnings' in result;
  }

  private escapeCSV(value: string | undefined | null): string {
    if (!value) return '';

    const stringValue = String(value);
    if (
      stringValue.includes('"') ||
      stringValue.includes(',') ||
      stringValue.includes('\n') ||
      stringValue.includes('\r')
    ) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  public downloadErrorsAndWarnings(): void {
    const csvRows: string[] = [];

    let rowNumber = 1;

    // Add failures
    if (this.data?.result?.failures && this.data.result.failures.length > 0) {
      this.data.result.failures.forEach(item => {
        const message = this.getItemOverviewText(item);
        csvRows.push(this.escapeCSV(`${rowNumber++}: ${message}`));
      });
    }

    // Add warnings (only available in TransactionStatusMetadata)
    if (this.data?.result && this.isTransactionStatusMetadata(this.data.result)) {
      if (this.data.result.warnings && this.data.result.warnings.length > 0) {
        this.data.result.warnings.forEach(warning => {
          const message = this.getItemOverviewText(warning);
          csvRows.push(this.escapeCSV(`${rowNumber++}: ${message}`));
        });
      }
    }

    // If no errors or warnings, don't download
    if (csvRows.length === 0) {
      return;
    }

    // Create CSV content with UTF-8 BOM for proper encoding (single column)
    const csvContent = `\uFEFF${csvRows.join('\n')}`;
    const fileName = `transaction-errors-warnings-${new Date().toISOString().split('T')[0]}.csv`;

    // Use DownloadFileHelper to download the CSV file
    const dataUri = `data:text/csv;charset=UTF-8,${encodeURIComponent(csvContent)}`;
    DownloadFileHelper.download(dataUri, fileName);
  }

  public hasErrorsOrWarnings(): boolean {
    const hasFailures = this.data?.result?.failures && this.data.result.failures.length > 0;
    const hasWarnings =
      this.data?.result &&
      this.isTransactionStatusMetadata(this.data.result) &&
      this.data.result.warnings &&
      this.data.result.warnings.length > 0;
    return hasFailures || hasWarnings;
  }
}
